import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart } from "lucide-react";
import { useCart } from "../context/CartContext";

export const Header = ({ links }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const menuButtonRef = useRef(null);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const close = e => { if (e.key === "Escape") { setMobileOpen(false); menuButtonRef.current?.focus(); } };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [mobileOpen]);
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const hasLocalSections = isHomePage || Boolean(links);
  const { cartCount, setShowCart } = useCart();

  const navLinks = links || [
    { label: "Курсы", href: "/courses", isRoute: true },
    { label: "Блог", href: "/blog", isRoute: true },
    { label: "Об авторе", href: "#author" },
    { label: "Отзывы", href: "#testimonials" },

    { label: "Обучение", href: "#learning" }
  ];

  const handleNavClick = (e, link) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    setMobileOpen(false);

    if (hasLocalSections) {
      const element = document.getElementById(link.href.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
    } else {
      navigate("/" + link.href);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      <div className="header-glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/images/brand/fokeev-wordmark-paper-v2.svg"
              alt="Fokeev"
              className="h-7 w-auto"
            />
          </Link>

          <nav aria-label={links ? "Разделы курса" : "Основная навигация"} className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="fokeev-nav-link text-sm font-medium text-gray-300 cursor-pointer"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={hasLocalSections ? link.href : "/" + link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className="fokeev-nav-link text-sm font-medium text-gray-300 cursor-pointer"
                >
                  {link.label}
                </a>
              )
            ))}
            {/* Payment service link - temporarily hidden */}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href="https://foundation.skillspace.ru/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-300 hover:text-teal-400">Мои курсы</a>
            <button
              onClick={() => setShowCart(true)}
              className="relative p-2 text-gray-300 hover:text-teal-400 transition-colors"
              aria-label="Открыть корзину"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <Link to="/courses" className="fokeev-primary-link">Все курсы</Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={() => setShowCart(true)}
              className="relative p-2 text-gray-300 hover:text-teal-400 transition-colors"
              aria-label="Открыть корзину"
              data-testid="cart-button-mobile"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label={mobileOpen ? "Закрыть меню" : "Открыть меню"}
              ref={menuButtonRef}
              aria-controls="mobile-navigation"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div id="mobile-navigation" className="lg:hidden max-h-[calc(100dvh-80px)] overflow-y-auto border-t border-white/10 bg-[#F1EDE4]/98 backdrop-blur-xl">
            <nav aria-label={links ? "Разделы курса" : "Основная навигация"} className="px-6 py-4 space-y-3">
              <a href="https://foundation.skillspace.ru/" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-teal-400 py-2">Мои курсы</a>
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="block text-gray-300 hover:text-teal-400 py-2 transition-colors cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={hasLocalSections ? link.href : "/" + link.href}
                    className="block text-gray-300 hover:text-teal-400 py-2 transition-colors cursor-pointer"
                    onClick={(e) => handleNavClick(e, link)}
                  >
                    {link.label}
                  </a>
                )
              ))}
              {/* Payment service link - mobile - temporarily hidden */}
              <Link to="/courses" onClick={() => setMobileOpen(false)} className="fokeev-primary-link w-full mt-3">Все курсы</Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
